import {
  autorun,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  AccommodationList as AccommodationListModel,
  AccommodationTypeList as AccommodationTypeListModel,
} from '@data/models';
import { AccommodationsService, AccommodationsTypeService } from '@data/api';
import TimeUtils from '@business/utils/time';
import Image from '@data/models/Image';
import Store from '../Store';

export default class Accommodations {
  @observable model: AccommodationListModel | null = null;
  @observable types: AccommodationTypeListModel | null = null;

  @observable checkin: string | null = null;
  @observable checkout: string | null = null;

  constructor() {
    makeObservable(this);
  }

  init() {
    autorun(async () => {
      if (
        this.propertyId &&
        this.language &&
        this.currency &&
        this.checkin &&
        this.checkout
      ) {
        await this.search(this.checkin, this.checkout);
      }
    });
  }

  getAccommodationName = (id: string, packageId?: string): string => {
    if (!this.types) return '';
    if (packageId) {
      const packageItem = this.types.items.find(
        (type) => type.id === id && type.packageId === packageId
      );
      if (packageItem) return packageItem.name;
    }
    const accommodation = this.types.items.find((type) => type.id === id);
    return accommodation?.name || '';
  };

  getAccommodationImages = (id: string, packageId?: string): Image[] => {
    if (!this.types) return [];
    const _packageId = packageId === '0' ? undefined : packageId;
    if (packageId) {
      const packageItem = this.types.items.find(
        (type) => type.id === id && type.packageId === _packageId
      );
      if (packageItem) return packageItem.images;
    }
    const accommodation = this.types.items.find((type) => type.id === id);
    return accommodation?.images || [];
  };

  loadAccommodationTypes = async (propertyId?: number) => {
    if (!propertyId) return;
    // passing a previous dates to get all types
    const result = await AccommodationsTypeService.load(propertyId);
    runInAction(() => {
      this.types = result;
    });

    return result;
  };

  search = async (checkin?: Date | string, checkout?: Date | string) => {
    const _checkin = checkin
      ? TimeUtils.format(checkin)
      : TimeUtils.format(TimeUtils.getDateOffset(0));
    const _checkout = checkout
      ? TimeUtils.format(checkout)
      : TimeUtils.format(TimeUtils.getDateOffset(1));

    runInAction(() => {
      this.model = null;
      this.checkin = _checkin;
      this.checkout = _checkout;
    });

    const result = !(this.propertyId && checkin && checkout)
      ? null
      : await AccommodationsService.search({
          widget_property: this.propertyId,
          checkin: _checkin,
          checkout: _checkout,
          lang: this.language,
          currency_code: this.currency,
        });

    runInAction(() => {
      this.model = result;
    });

    return result;
  };

  @computed private get language() {
    return Store.environment.language || 'en';
  }

  @computed private get currency() {
    return Store.environment.currency || 'usd';
  }

  @computed private get propertyId() {
    return Store.property.model?.id;
  }
}
